import React from 'react';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, Award, Heart, Handshake } from 'lucide-react';

const Impact = () => {
  // UN SDGs - 5 primary alignment per Impact Strategy 2026-2031
  const sdgGoals = [
    { 
      number: 7, 
      title: 'Affordable & Clean Energy',
      icon: 'https://customer-assets.emergentagent.com/job_live-builder-10/artifacts/s38xyzvv_TheGlobalGoals_Icons_Color_Goal_7.png',
      color: 'bg-[#FCC30B]'
    },
    { 
      number: 9, 
      title: 'Industry, Innovation & Infrastructure',
      icon: 'https://customer-assets.emergentagent.com/job_live-builder-10/artifacts/81b7teku_TheGlobalGoals_Icons_Color_Goal_9.png',
      color: 'bg-[#FD6925]'
    },
    { 
      number: 11, 
      title: 'Sustainable Cities & Communities',
      icon: 'https://customer-assets.emergentagent.com/job_live-builder-10/artifacts/zd1p1me0_TheGlobalGoals_Icons_Color_Goal_11.png',
      color: 'bg-[#FD9D24]'
    },
    { 
      number: 13, 
      title: 'Climate Action',
      icon: 'https://customer-assets.emergentagent.com/job_live-builder-10/artifacts/87h6cvbb_TheGlobalGoals_Icons_Color_Goal_13.png',
      color: 'bg-[#3F7E44]'
    },
    { 
      number: 16, 
      title: 'Peace, Justice & Strong Institutions',
      icon: 'https://customer-assets.emergentagent.com/job_live-builder-10/artifacts/xipqqwjd_TheGlobalGoals_Icons_Color_Goal_16.png',
      color: 'bg-[#00689D]'
    },
  ];

  // The Just Transition Index — Queensland's Energy Performance Scorecard (First Edition)
  const justiceFramework = [
    {
      question: 'Do Queenslanders understand the transition?',
      score: 34.6,
      rating: 'Poor',
      image: 'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?auto=format&fit=crop&w=800&q=80',
    },
    {
      question: 'Can Queenslanders make energy-related decisions that work for them?',
      score: 65.5,
      rating: 'Good',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=800&q=80',
    },
    {
      question: 'How is our energy transition progressing?',
      score: 33.3,
      rating: 'Poor',
      image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=800&q=80',
    },
    {
      question: 'Are communities prepared and resilient?',
      score: 42.0,
      rating: 'Fair',
      image: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=800&q=80',
    },
    {
      question: 'Are Queenslanders benefiting from energy?',
      score: 23.6,
      rating: 'Poor',
      image: 'https://images.unsplash.com/photo-1497486751825-1233686d5d80?auto=format&fit=crop&w=800&q=80',
    },
  ];

  const ratingStyles = {
    Poor: { text: 'text-red-400', badge: 'bg-red-500/20 text-red-300 border-red-400/40' },
    Fair: { text: 'text-yellow-300', badge: 'bg-yellow-500/20 text-yellow-200 border-yellow-400/40' },
    Good: { text: 'text-emerald-300', badge: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40' },
  };

  // Memberships & Affiliations
  const memberships = [
    { 
      name: 'Queensland Social Enterprise Council', 
      short: 'QSEC',
      logo: null
    },
    { 
      name: 'Loss and Damage Collective', 
      short: 'L&D Collective',
      logo: null
    },
    { 
      name: 'Social Impact Measurement Network Australia', 
      short: 'SIMNA',
      logo: null
    },
    { 
      name: 'Better Futures Australia', 
      short: 'BFA',
      logo: null
    },
    { 
      name: 'Creatives for Climate', 
      short: 'C4C',
      logo: null
    },
  ];

  const caseStudies = [
    {
      title: 'Envision the Future',
      client: 'World Energy Council',
      year: '2025-2026',
      description: 'Living Foresight System - developing an inclusive and dynamic global foresight platform.',
      outcomes: ['Online foresight tool deployed', 'Pilot system implemented', 'Comprehensive playbook delivered'],
      image: 'https://images.pexels.com/photos/414905/pexels-photo-414905.jpeg'
    },
    {
      title: 'Enable the Change',
      client: 'Indigenous Land & Sea Corporation',
      year: '2024',
      description: 'Energy Investment Analysis - identifying and maximizing benefits for First Nations in renewable energy transition.',
      outcomes: ['Engagement forums delivered', 'Strategic reports completed', 'Investment pathways informed'],
      image: 'https://images.pexels.com/photos/2469122/pexels-photo-2469122.jpeg'
    },
    {
      title: 'Deliver the Impact',
      client: 'DT Global Asia Pacific (for DFAT)',
      year: '2025-2026',
      description: 'Climate Strategy - developed climate change and disaster risk reduction strategy for a health transformation program in Indonesia.',
      outcomes: ['Foundational Climate Change and Disaster Risk Reduction Strategy', 'Phased program capability improvement plan', 'MEL framework established'],
      image: 'https://images.unsplash.com/photo-1719825523711-eda3221c111c'
    }
  ];

  return (
    <div data-testid="impact-page" className="bg-ozone-white min-h-screen">
      <Helmet>
        <title>Our Impact | UN SDGs & Closing the Gap | Sustainify® | Rachelle Cooper Kulkarni</title>
        <meta name="description" content="Sustainify®'s impact framework — aligned with UN Sustainable Development Goals, Closing the Gap, and a Just Transition. Real client work for World Energy Council, Indigenous Land & Sea Corporation, and DFAT." />
        <link rel="canonical" href="https://www.sustainify.com.au/impact" />
      </Helmet>
      {/* Hero */}
      <div className="bg-basalt-carbon text-ozone-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="impact-title">
            Our <span className="text-solar-kinetic">Impact</span>
          </h1>
          <p className="text-xl text-ozone-white/80 max-w-3xl">
            Measuring what matters. Delivering real-world outcomes aligned with global sustainability goals and the National Agreement on Closing the Gap.
          </p>
        </div>
      </div>

      {/* Impact Framework - Our Vision to Foundation */}
      <section className="py-20 bg-ozone-white" data-testid="just-transition-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-4xl mx-auto">
            <span className="inline-block bg-solar-kinetic/20 text-kabi-teal px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Our Impact Framework
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-basalt-carbon mb-4 leading-tight">
              From <span className="text-kabi-teal">Our Vision</span> to Foundation
            </h2>
            <p className="text-lg text-basalt-carbon/70 leading-relaxed">
              How everything we do connects — from our highest vision down to our foundational accountability.
            </p>
          </div>

          {/* OUR VISION */}
          <div className="relative mb-4" data-testid="framework-north-star">
            <div className="bg-gradient-to-r from-solar-kinetic to-solar-kinetic/80 rounded-2xl p-8 text-center shadow-xl border-2 border-solar-kinetic">
              <div className="text-xs font-bold uppercase tracking-widest text-basalt-carbon/70 mb-2">★ Our Vision</div>
              <h3 className="text-2xl md:text-3xl font-bold text-basalt-carbon mb-2 leading-tight">
                Inclusive and thriving futures for people and planet
              </h3>
              <p className="text-base text-basalt-carbon/80 italic">
                for communities, ecosystems and economies
              </p>
            </div>
            <div className="flex justify-center"><div className="w-1 h-8 bg-basalt-carbon/30"></div></div>
          </div>

          {/* A JUST TRANSITION */}
          <div className="relative mb-4" data-testid="framework-just-transition">
            <div className="bg-kabi-teal text-ozone-white rounded-2xl p-6 text-center shadow-lg max-w-2xl mx-auto">
              <div className="text-xs font-bold uppercase tracking-widest text-solar-kinetic mb-2">The Pathway</div>
              <h3 className="text-2xl md:text-3xl font-bold">A Just Transition</h3>
            </div>
            <div className="flex justify-center"><div className="w-1 h-8 bg-basalt-carbon/30"></div></div>
          </div>

          {/* THE SYSTEMIC CHANGE OUTCOMES block removed per user request */}

          {/* OUR STRATEGIES section removed per user request */}

          {/* HOW WE DELIVER section removed per user request */}

          {/* THE JUST TRANSITION INDEX — Queensland's Energy Performance Scorecard */}
          <div data-testid="just-transition-index">
            <div className="bg-gradient-to-br from-basalt-carbon to-kabi-teal text-ozone-white rounded-2xl p-8 md:p-12 shadow-xl">
              <div className="text-center mb-8 max-w-3xl mx-auto">
                <span className="inline-block bg-solar-kinetic/20 text-solar-kinetic px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                  Scorecard · First Edition
                </span>
                <h3 className="text-3xl md:text-5xl font-bold mb-3 leading-tight">
                  The <span className="text-solar-kinetic">Just Transition Index</span>
                </h3>
                <p className="text-lg md:text-xl text-ozone-white/85 italic">
                  Queensland's energy performance scorecard
                </p>
              </div>

              {/* Descriptive text */}
              <div className="max-w-3xl mx-auto mb-10 bg-ozone-white/5 border border-solar-kinetic/20 rounded-xl p-6">
                <h4 className="text-xl md:text-2xl font-bold text-solar-kinetic mb-3">
                  Is Queensland's energy transition fair?
                </h4>
                <p className="text-base md:text-lg text-ozone-white/85 leading-relaxed">
                  We're building a Queensland-wide index that gives a score out of 100 for five practical questions: <span className="text-ozone-white font-semibold">Do Queenslanders understand the transition? Can they make energy decisions that work for them? Are we transitioning on plan? How prepared are we? Who is benefiting?</span> Every score is built from public data and shows its working.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
                {justiceFramework.map((item, idx) => {
                  const styles = ratingStyles[item.rating] || ratingStyles.Fair;
                  return (
                    <div
                      key={idx}
                      data-testid={`justice-pillar-${idx}`}
                      className="bg-ozone-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-solar-kinetic/30 hover:border-solar-kinetic hover:bg-ozone-white/15 transition-all group flex flex-col"
                    >
                      <div className="h-32 w-full overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.question}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5 flex flex-col flex-grow">
                        <h5 className="text-sm font-semibold text-ozone-white leading-snug mb-4 min-h-[48px]">
                          {item.question}
                        </h5>
                        <div className="mt-auto pt-3 border-t border-solar-kinetic/20">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-solar-kinetic/80 mb-1">
                            Score
                          </div>
                          <div className="flex items-baseline justify-between mb-2">
                            <span className={`text-3xl font-bold ${styles.text}`} data-testid={`pillar-score-${idx}`}>
                              {item.score}
                            </span>
                            <span className="text-xs text-ozone-white/50 font-medium">/ 100</span>
                          </div>
                          <span
                            data-testid={`pillar-rating-${idx}`}
                            className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${styles.badge}`}
                          >
                            {item.rating}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center">
                <a
                  href="mailto:info@sustainify.com.au?subject=Request%3A%20Explore%20the%20Just%20Transition%20Index&body=Hi%20Sustainify%20team%2C%0A%0AI%27d%20like%20to%20explore%20the%20Just%20Transition%20Index%20%E2%80%94%20Queensland%27s%20Energy%20Performance%20Scorecard.%0A%0AName%3A%20%0AOrganisation%3A%20%0ARole%3A%20%0A%0AThanks%21"
                  data-testid="jti-cta"
                  className="inline-flex items-center space-x-2 bg-solar-kinetic text-basalt-carbon px-8 py-4 rounded-full font-semibold hover:bg-solar-kinetic/90 transition-all shadow-lg"
                >
                  <span>Explore the Just Transition Index</span>
                  <TrendingUp size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Strategy CTA — standalone lead-gen */}
      <section className="py-16 bg-ozone-white" data-testid="impact-strategy-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-basalt-carbon text-ozone-white rounded-2xl p-8 md:p-10 text-center shadow-lg border-t-4 border-solar-kinetic">
            <span className="inline-block bg-solar-kinetic/20 text-solar-kinetic px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              Deeper Dive
            </span>
            <h3 className="text-2xl md:text-3xl font-bold mb-3 leading-tight">
              Want to learn more about our <span className="text-solar-kinetic">Impact Strategy</span>?
            </h3>
            <p className="text-base text-ozone-white/80 mb-6 max-w-2xl mx-auto leading-relaxed">
              Get the full picture of how we translate our vision into measurable outcomes across communities, ecosystems and economies.
            </p>
            <a
              href="mailto:info@sustainify.com.au?subject=Request%3A%20See%20Our%20Impact%20Strategy&body=Hi%20Sustainify%20team%2C%0A%0AI%27d%20love%20to%20see%20your%20Impact%20Strategy.%0A%0AName%3A%20%0AOrganisation%3A%20%0ARole%3A%20%0A%0AThanks%21"
              data-testid="impact-strategy-cta"
              className="inline-flex items-center space-x-2 bg-solar-kinetic text-basalt-carbon px-8 py-4 rounded-full font-semibold hover:bg-solar-kinetic/90 transition-all"
            >
              <span>See Our Impact Strategy</span>
              <Award size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* SDG Framework */}
      <section className="py-20 bg-white" data-testid="sdg-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-kabi-teal/10 text-kabi-teal px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Global Framework
            </span>
            <h2 className="text-4xl font-bold text-basalt-carbon mb-4">
              Aligned with UN <span className="text-kabi-teal">Sustainable Development Goals</span>
            </h2>
            <p className="text-lg text-basalt-carbon/70 max-w-3xl mx-auto">
              Sustainify® positively affects all SDGs through systems impact, with primary focus on these five priority goals.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {sdgGoals.map((goal) => (
              goal.icon ? (
                <a 
                  key={goal.number}
                  href={`https://www.un.org/sustainabledevelopment/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`sdg-badge-${goal.number}`}
                  className="rounded-lg overflow-hidden hover:scale-105 transition-transform shadow-lg block"
                >
                  <img 
                    src={goal.icon} 
                    alt={`UN SDG ${goal.number}: ${goal.title}`}
                    className="w-full h-full object-cover"
                  />
                </a>
              ) : (
                <div 
                  key={goal.number}
                  data-testid={`sdg-badge-${goal.number}`}
                  className={`${goal.color} text-white rounded-lg p-6 flex flex-col items-center justify-center text-center min-h-[140px] hover:scale-105 transition-transform shadow-lg`}
                >
                  <div className="text-4xl font-bold mb-2">{goal.number}</div>
                  <div className="text-xs font-semibold uppercase tracking-wide leading-tight">{goal.title}</div>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Indigenous Partnership Statement */}
      <section className="py-20 bg-basalt-carbon text-ozone-white" data-testid="indigenous-partnership-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-solar-kinetic text-basalt-carbon w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Handshake size={32} />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Working with <span className="text-solar-kinetic">First Nations</span> Communities
              </h2>
            </div>
            <div>
              <p className="text-xl text-ozone-white/95 leading-relaxed mb-5">
                Sustainify® is an <strong className="text-solar-kinetic">ally business that embodies Indigenous wisdom within our operating structures, led by advice from Elders</strong>. We work closely with Indigenous businesses and communities across Australia as genuine partners.
              </p>
              <p className="text-base text-ozone-white/80 italic border-l-4 border-solar-kinetic pl-5 mt-6 leading-relaxed">
                We acknowledge the Kabi Kabi people as the Traditional Custodians and intergenerational stewards of the Sunshine Coast. We extend our acknowledgement and solidarity to all Aboriginal and Torres Strait Islander peoples across Australia. <span className="text-solar-kinetic font-semibold not-italic">Always was, always will be.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Metrics - Moved to after Case Studies */}

      {/* Memberships & Affiliations - Section moved below case studies */}

      {/* Case Studies */}
      <section className="py-20 bg-ozone-white" data-testid="case-studies-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-basalt-carbon mb-4 text-center">
            Real Work. Real Clients. <span className="text-kabi-teal">Real Outcomes.</span>
          </h2>
          <p className="text-center text-basalt-carbon/70 mb-12 max-w-2xl mx-auto">
            We deliver real-world outcomes that are not only fit for immediate needs, but future-proof your legacy impact.
          </p>
          <div className="space-y-12">
            {caseStudies.map((study, index) => (
              <div 
                key={index}
                data-testid={`case-study-${index}`}
                className="grid md:grid-cols-2 gap-8 items-center"
              >
                {index % 2 === 0 ? (
                  <>
                    <div>
                      <img 
                        src={study.image}
                        alt={study.title}
                        className="rounded-2xl shadow-lg w-full h-80 object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-basalt-carbon mb-2">{study.title}</h3>
                      <p className="text-kabi-teal font-semibold mb-1">{study.client}</p>
                      <p className="text-basalt-carbon/60 text-sm mb-4">{study.year}</p>
                      <p className="text-basalt-carbon/80 mb-6 leading-relaxed">{study.description}</p>
                      <div>
                        <p className="font-semibold text-basalt-carbon mb-3">Outcomes:</p>
                        <ul className="space-y-2">
                          {study.outcomes.map((outcome, idx) => (
                            <li key={idx} className="flex items-start text-basalt-carbon/70">
                              <span className="text-solar-kinetic mr-2">✓</span>
                              <span>{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="order-2 md:order-1">
                      <h3 className="text-3xl font-bold text-basalt-carbon mb-2">{study.title}</h3>
                      <p className="text-kabi-teal font-semibold mb-1">{study.client}</p>
                      <p className="text-basalt-carbon/60 text-sm mb-4">{study.year}</p>
                      <p className="text-basalt-carbon/80 mb-6 leading-relaxed">{study.description}</p>
                      <div>
                        <p className="font-semibold text-basalt-carbon mb-3">Outcomes:</p>
                        <ul className="space-y-2">
                          {study.outcomes.map((outcome, idx) => (
                            <li key={idx} className="flex items-start text-basalt-carbon/70">
                              <span className="text-solar-kinetic mr-2">✓</span>
                              <span>{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="order-1 md:order-2">
                      <img 
                        src={study.image}
                        alt={study.title}
                        className="rounded-2xl shadow-lg w-full h-80 object-cover"
                      />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Metrics section removed per user request */}

      {/* Memberships & Affiliations - Now After Case Studies */}
      <section className="py-20 bg-white" data-testid="memberships-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-kabi-teal/10 text-kabi-teal px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Networks & Alliances
            </span>
            <h2 className="text-4xl font-bold text-basalt-carbon mb-4">
              Proud <span className="text-kabi-teal">Member</span> Of
            </h2>
            <p className="text-lg text-basalt-carbon/70 max-w-3xl mx-auto">
              We stand alongside leading networks driving social enterprise, climate action, and systems change across Australia and beyond.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {memberships.map((member, index) => (
              <div 
                key={index}
                data-testid={`membership-${index}`}
                className="bg-white border-2 border-kabi-teal/20 rounded-2xl p-6 text-center hover:border-kabi-teal hover:shadow-lg transition-all flex flex-col items-center justify-center min-h-[200px]"
              >
                {member.logo ? (
                  <div className="h-20 w-full flex items-center justify-center mb-3">
                    <img 
                      src={member.logo} 
                      alt={`${member.name} logo`}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="bg-kabi-teal text-white w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Heart size={24} />
                  </div>
                )}
                <div className="text-xs font-bold text-kabi-teal uppercase tracking-wide mb-2">
                  {member.short}
                </div>
                <h3 className="text-sm font-semibold text-basalt-carbon leading-tight">{member.name}</h3>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-basalt-carbon/60 mt-8 italic">
            Aligned to QSEC standards and People & Planet First global standards.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Impact;
